import { useQueryClient } from '@tanstack/react-query'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { setUnauthenticatedHandler } from './client'
import { API_MODE, type ApiMode } from './config'

export type SessionUser = {
  id: string
  name?: string | null
  username?: string | null
}

export type Session = {
  mode: ApiMode
  token: string
  expires?: string | null
  user?: SessionUser | null
}

type AuthState = {
  /** Selected environment. Persisted even while signed out. */
  mode: ApiMode
  setMode: (mode: ApiMode) => void
  session: Session | null
  signIn: (session: Session) => void
  signOut: () => void
  /** True once localStorage has been read (avoids SSR/hydration mismatch). */
  ready: boolean
}

const SESSION_KEY = 'galley.session'
const MODE_KEY = 'galley.mode'

const AuthContext = createContext<AuthState | null>(null)

function readSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Session
    if (!parsed?.token || parsed.mode !== API_MODE) return null
    if (parsed.expires && new Date(parsed.expires).getTime() < Date.now()) return null
    return parsed
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [mode, setModeState] = useState<ApiMode>(API_MODE)
  const [ready, setReady] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    const stored = readSession()
    if (stored) {
      setSession(stored)
      setModeState(stored.mode)
    }
    setReady(true)
  }, [])

  const setMode = useCallback(
    (next: ApiMode) => {
      if (next !== API_MODE) return
      setSession(null)
      try {
        localStorage.removeItem(SESSION_KEY)
      } catch {
        /* In-memory sign-out still works. */
      }
      setModeState(next)
      try {
        localStorage.setItem(MODE_KEY, next)
      } catch {
        /* Use the deployment-bound mode. */
      }
      // Different environment, different data.
      queryClient.clear()
    },
    [queryClient],
  )

  const signIn = useCallback(
    (next: Session) => {
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify(next))
        localStorage.setItem(MODE_KEY, next.mode)
      } catch {
        /* This session remains available in memory. */
      }
      setSession(next)
      setModeState(next.mode)
      queryClient.clear()
    },
    [queryClient],
  )

  const signOut = useCallback(() => {
    try {
      localStorage.removeItem(SESSION_KEY)
    } catch {
      /* In-memory sign-out still works. */
    }
    setSession(null)
    queryClient.clear()
  }, [queryClient])

  // An expired or rejected token must never linger in storage.
  useEffect(() => {
    setUnauthenticatedHandler(() => signOut())
    return () => setUnauthenticatedHandler(null)
  }, [signOut])

  const value = useMemo<AuthState>(() => ({ mode, setMode, session, signIn, signOut, ready }), [mode, setMode, session, signIn, signOut, ready])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/** The session shape the GraphQL client needs. */
export function useRequestSession() {
  const { mode, session } = useAuth()
  return useMemo(() => ({ mode: session?.mode ?? mode, token: session?.token ?? null }), [mode, session])
}
